/**
 * Meetings at shows (decision of Nov 3, 2026): rules shared by the web app,
 * the API and the worker.
 */

/** A date as YYYY-MM-DD, read in UTC. */
const iso = (d: Date) => d.toISOString().slice(0, 10);

/**
 * The next step's date by default: three working days (Monday to Friday)
 * after the show's last day, or after the meeting's day without a show.
 */
export function defaultNextStepDue(after: string): string {
  const d = new Date(`${after.slice(0, 10)}T12:00:00Z`);
  let left = 3;
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    const day = d.getUTCDay();
    if (day !== 0 && day !== 6) left -= 1;
  }
  return iso(d);
}

/**
 * The meeting's title in HubSpot (Nov 4, 2026): with whom (the company, else
 * the contact's name) and at which show, by its name: "Meeting with
 * Terramet Recycling (Ecomondo 2026)".
 */
export function meetingTitle(who: string | null | undefined, eventName: string | null | undefined) {
  const w = (who ?? '').trim();
  const at = (eventName ?? '').trim();
  return `Meeting${w ? ` with ${w}` : ''}${at ? ` (${at})` : ''}`.slice(0, 250);
}

/**
 * Where the meeting took place, in HubSpot (Nov 4, 2026): the show's name
 * and its city ("Ecomondo 2026, Rimini"); nothing without a show.
 */
export function meetingLocation(
  event: { series?: string | null; year?: number | null; location?: string | null } | null,
): string | null {
  if (!event) return null;
  const name = [event.series, event.year]
    .filter((v) => v !== null && v !== undefined && v !== '')
    .join(' ');
  const city = (event.location ?? '').split(',')[0]!.trim();
  return [name, city].filter(Boolean).join(', ') || null;
}

/** How long a meeting lasts in HubSpot when only its time is known. */
export const MEETING_MINUTES = 30;

/**
 * A meeting documents what took place (Nov 4, 2026): in HubSpot it ends at
 * its time (never later than now) and began half an hour before, both in
 * the past, so HubSpot never shows it as one to come.
 */
export function completedMeetingTimes(metAt: Date, now: Date): { start: Date; end: Date } {
  const end = new Date(Math.min(metAt.getTime(), now.getTime()));
  return { start: new Date(end.getTime() - MEETING_MINUTES * 60_000), end };
}
